"use client";

import { useEffect, useRef } from "react";

/**
 * Locks the document scroll in a robust way (works on iOS Safari),
 * preserving the existing scroll position and restoring it on unlock.
 *
 * Why not just `overflow: hidden` on body? iOS Safari ignores it and keeps
 * scrolling the page behind the overlay. The reliable technique is
 * `position: fixed` on body with a negative `top` equal to the scroll offset,
 * which visually freezes the page in place; on unlock we scroll back to the
 * remembered offset.
 */
export function useBodyScrollLock(locked: boolean) {
  const lockedScrollYRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    const applyLock = () => {
      if (bodyEl.style.position === "fixed") return; // already locked (idempotent for the re-apply listeners)
      lockedScrollYRef.current = window.scrollY || window.pageYOffset || 0;
      // Temporarily disable CSS smooth scrolling so the position juggling below
      // is instant instead of visibly animating.
      const prevScrollBehavior = htmlEl.style.scrollBehavior;
      htmlEl.style.scrollBehavior = "auto";

      htmlEl.style.overflow = "hidden";
      htmlEl.style.overscrollBehavior = "contain";

      bodyEl.style.position = "fixed";
      bodyEl.style.top = `-${lockedScrollYRef.current}px`;
      bodyEl.style.left = "0";
      bodyEl.style.right = "0";
      bodyEl.style.width = "100%";
      bodyEl.style.overscrollBehavior = "contain";

      // Desktop: hiding the scrollbar widens the viewport, which shifts the whole
      // layout sideways — compensate with equivalent padding.
      const scrollbarWidth = window.innerWidth - htmlEl.clientWidth;
      if (scrollbarWidth > 0) {
        bodyEl.style.paddingRight = `${scrollbarWidth}px`;
      }

      requestAnimationFrame(() => {
        htmlEl.style.scrollBehavior = prevScrollBehavior;
      });
    };

    const removeLock = () => {
      const y = lockedScrollYRef.current;
      const prevScrollBehavior = htmlEl.style.scrollBehavior;
      htmlEl.style.scrollBehavior = "auto";

      htmlEl.style.overflow = "";
      htmlEl.style.overscrollBehavior = "";

      bodyEl.style.position = "";
      bodyEl.style.top = "";
      bodyEl.style.left = "";
      bodyEl.style.right = "";
      bodyEl.style.width = "";
      bodyEl.style.overscrollBehavior = "";
      bodyEl.style.paddingRight = "";

      window.scrollTo(0, y);
      requestAnimationFrame(() => {
        htmlEl.style.scrollBehavior = prevScrollBehavior;
      });
    };

    const reapplyIfLocked = () => {
      if (locked) {
        applyLock();
      }
    };

    if (locked) {
      applyLock();
      // Mobile browsers (iOS especially) can silently drop the fixed-body styles
      // after a tab switch, bfcache restore, rotation or resize — re-assert the
      // lock on every event that can trigger that. applyLock is idempotent.
      document.addEventListener("visibilitychange", reapplyIfLocked);
      window.addEventListener("pageshow", reapplyIfLocked);
      window.addEventListener("focus", reapplyIfLocked);
      window.addEventListener("orientationchange", reapplyIfLocked);
      window.addEventListener("resize", reapplyIfLocked);
    } else {
      removeLock();
    }

    return () => {
      document.removeEventListener("visibilitychange", reapplyIfLocked);
      window.removeEventListener("pageshow", reapplyIfLocked);
      window.removeEventListener("focus", reapplyIfLocked);
      window.removeEventListener("orientationchange", reapplyIfLocked);
      window.removeEventListener("resize", reapplyIfLocked);
      if (!locked) return;
      removeLock();
    };
  }, [locked]);
}


